import { StockType } from 'types/stock'
import { CheckoutItemType } from 'types/checkoutItem'
import { useEffect, useState } from 'react'
import { sumCheckoutItemsAmount, sumStocksAmount } from 'lib/helper'
import { BreadType } from 'types/bread'
import { FirebaseDocRefType } from 'firebase/index'
import { ScheduleType } from 'types/schedule'
import { addDefaultStock } from 'lib/stock'

// 注文された後に在庫データが削除されてしまった場合（有効在庫数がマイナスの場合）に在庫データを生成する
const useAdjustStock = (
  breads: BreadType[],
  checkoutItems: CheckoutItemType[],
  date: string,
  latestOrderStartTime: string,
  lastOrderTime: string,
  storeName: string,
  schedules: ScheduleType[],
  stocks: StockType[],
  storeRef: FirebaseDocRefType,
  checkedOrderTypes: string[]
): StockType[] => {
  const [stockList, setStockList] = useState<StockType[]>([])

  useEffect(() => {
    if (!stocks || !checkoutItems) return
    let nextStocks: StockType[] = []

    for (const type of checkedOrderTypes) {
      for (let i = 0; i < breads.length; i = (i + 1) | 0) {
        const bread = breads[i]
        if (!bread.bread_ref) continue
        const stocksOfBread = stocks.filter(
          (stock) => stock.bread_ref.id === bread.bread_ref?.id && stock.type === type
        )
        const checkoutItemsOfBread = checkoutItems.filter(
          (checkoutItem) => checkoutItem.bread_ref.id === bread.bread_ref?.id && checkoutItem.type === type
        )
        const availableStockAmount = sumStocksAmount(stocksOfBread) - sumCheckoutItemsAmount(checkoutItemsOfBread)
        // 有効在庫数が0以上ならそのまま追加する
        if (availableStockAmount >= 0) {
          nextStocks = [...nextStocks, ...stocksOfBread]
          continue
        }
        // 有効在庫数がマイナスの場合は在庫データを追加
        const pickupableTimes = checkoutItemsOfBread.reduce((prev: string[], checkoutItem) => {
          return prev.includes(checkoutItem.pickupable_time) ? prev : [...prev, checkoutItem.pickupable_time]
        }, [])
        const adjustStocks = pickupableTimes
          .reduce((prev: StockType[], pickupableTime) => {
            const stock = stocksOfBread.find((stock) => stock.pickupable_time === pickupableTime)
            const checkoutItemsAmount = sumCheckoutItemsAmount(checkoutItemsOfBread, pickupableTime)
            // 注文データに存在する受渡解禁時刻の在庫データが存在しない場合
            if (!stock) {
              // 当日取り置きのみこの処理を適用する
              if (type !== 'daily') return prev
              const schedule = schedules.find(
                (schedule) => schedule.bread_ref.id === bread.bread_ref?.id && schedule.type === type
              )
              // 当日取り置き設定が存在しない場合は在庫データを追加しない
              if (!schedule) return prev
              const stockRef = storeRef
                .collection('store_stocks')
                .doc(`stock_${type}_${schedule.schedule_ref?.id}-${date}_${pickupableTime}`)
              return [
                ...prev,
                addDefaultStock(
                  bread,
                  storeRef,
                  schedule,
                  stockRef,
                  latestOrderStartTime,
                  lastOrderTime,
                  storeName,
                  date,
                  pickupableTime,
                  checkoutItemsAmount
                ),
              ]
            } else {
              // 在庫データは存在するが有効在庫数がマイナスと判定された場合
              stock.stock = checkoutItemsAmount
              return [...prev, stock]
            }
          }, [])
          .sort((a, b) => (a.pickupable_time < b.pickupable_time ? -1 : 1))
        // 調整済みのstocksを追加
        nextStocks = [...nextStocks, ...adjustStocks]
      }
      setStockList([...nextStocks])
    }
  }, [
    breads,
    checkoutItems,
    date,
    latestOrderStartTime,
    lastOrderTime,
    storeName,
    schedules,
    stocks,
    storeRef,
    checkedOrderTypes,
  ])
  return stockList
}

export default useAdjustStock
